import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Send, CheckCircle, X } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const translations = {
  nl: {
    title: 'Schrijf een Review',
    subtitle: 'Deel je ervaring met Netblitz',
    name: 'Je naam *',
    company: 'Bedrijfsnaam',
    email: 'E-mailadres *',
    projectType: 'Type project',
    reviewTitle: 'Titel van je review *',
    content: 'Je ervaring *',
    rating: 'Beoordeling *',
    submit: 'Review versturen',
    submitting: 'Versturen...',
    success: 'Bedankt voor je review! Deze wordt na goedkeuring geplaatst.',
    error: 'Er ging iets mis. Probeer het opnieuw.',
    thanks: 'Bedankt!',
    close: 'Sluiten',
    select: '-- Selecteer --',
    projectTypes: ['Website', 'Webshop', 'SEO', 'Redesign', 'Onderhoud', 'Anders'],
    placeholders: {
      name: 'Jan Jansen',
      company: 'Jouw Bedrijf B.V.',
      email: 'jan@voorbeeld.nl',
      title: 'Uitstekende service!',
      content: 'Vertel over je ervaring met Netblitz...'
    }
  },
  en: {
    title: 'Write a Review',
    subtitle: 'Share your experience with Netblitz',
    name: 'Your name *',
    company: 'Company name',
    email: 'Email address *',
    projectType: 'Project type',
    reviewTitle: 'Review title *',
    content: 'Your experience *',
    rating: 'Rating *',
    submit: 'Submit review',
    submitting: 'Submitting...',
    success: 'Thank you for your review! It will be published after approval.',
    error: 'Something went wrong. Please try again.',
    thanks: 'Thank you!',
    close: 'Close',
    select: '-- Select --',
    projectTypes: ['Website', 'Webshop', 'SEO', 'Redesign', 'Maintenance', 'Other'],
    placeholders: {
      name: 'John Doe',
      company: 'Your Company Ltd.',
      email: 'john@example.com',
      title: 'Excellent service!',
      content: 'Tell us about your experience with Netblitz...'
    }
  },
  fr: {
    title: 'Écrire un avis',
    subtitle: 'Partagez votre expérience avec Netblitz',
    name: 'Votre nom *',
    company: 'Nom de l\'entreprise',
    email: 'Adresse email *',
    projectType: 'Type de projet',
    reviewTitle: 'Titre de l\'avis *',
    content: 'Votre expérience *',
    rating: 'Note *',
    submit: 'Envoyer l\'avis',
    submitting: 'Envoi...',
    success: 'Merci pour votre avis! Il sera publié après approbation.',
    error: 'Une erreur s\'est produite. Veuillez réessayer.',
    thanks: 'Merci!',
    close: 'Fermer',
    select: '-- Sélectionner --',
    projectTypes: ['Site web', 'Boutique', 'SEO', 'Refonte', 'Maintenance', 'Autre'],
    placeholders: {
      name: 'Jean Dupont',
      company: 'Votre Entreprise SARL',
      email: 'jean@exemple.fr',
      title: 'Excellent service!',
      content: 'Parlez-nous de votre expérience avec Netblitz...'
    }
  },
  de: {
    title: 'Bewertung schreiben',
    subtitle: 'Teilen Sie Ihre Erfahrung mit Netblitz',
    name: 'Ihr Name *',
    company: 'Firmenname',
    email: 'E-Mail-Adresse *',
    projectType: 'Projekttyp',
    reviewTitle: 'Titel der Bewertung *',
    content: 'Ihre Erfahrung *',
    rating: 'Bewertung *',
    submit: 'Bewertung senden',
    submitting: 'Wird gesendet...',
    success: 'Vielen Dank für Ihre Bewertung! Sie wird nach Genehmigung veröffentlicht.',
    error: 'Etwas ist schief gelaufen. Bitte versuchen Sie es erneut.',
    thanks: 'Danke!',
    close: 'Schließen',
    select: '-- Auswählen --',
    projectTypes: ['Website', 'Webshop', 'SEO', 'Redesign', 'Wartung', 'Andere'],
    placeholders: {
      name: 'Max Mustermann',
      company: 'Ihre Firma GmbH',
      email: 'max@beispiel.de',
      title: 'Ausgezeichneter Service!',
      content: 'Erzählen Sie uns von Ihrer Erfahrung mit Netblitz...'
    }
  }
};

export const ReviewForm = ({ onClose, onSuccess }) => {
  const { language } = useLanguage();
  const t = translations[language] || translations.nl;
  
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    rating: 5,
    title: '',
    content: '',
    project_type: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${BACKEND_URL}/api/reviews`, formData);
      setIsSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(t.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold text-[#0f172a] mb-2">{t.thanks}</h3>
        <p className="text-[#475569]">{t.success}</p>
        {onClose && (
          <button
            onClick={onClose}
            className="mt-6 px-6 py-2 bg-[#2563EB] text-white rounded-xl hover:bg-[#1D4ED8] transition-colors"
          >
            {t.close}
          </button>
        )}
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Rating */}
      <div>
        <label className="block text-sm font-medium text-[#0f172a] mb-2">{t.rating}</label>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setFormData({ ...formData, rating: star })}
              className="p-1 transition-transform hover:scale-110"
            >
              <Star
                size={28}
                className={star <= formData.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Name & Company */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#0f172a] mb-2">{t.name}</label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={t.placeholders.name}
            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB] outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0f172a] mb-2">{t.company}</label>
          <input
            type="text"
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            placeholder={t.placeholders.company}
            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB] outline-none"
          />
        </div>
      </div>

      {/* Email & Project Type */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#0f172a] mb-2">{t.email}</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder={t.placeholders.email}
            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB] outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0f172a] mb-2">{t.projectType}</label>
          <select
            value={formData.project_type}
            onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB] outline-none bg-white"
          >
            <option value="">{t.select}</option>
            {t.projectTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Review Title */}
      <div>
        <label className="block text-sm font-medium text-[#0f172a] mb-2">{t.reviewTitle}</label>
        <input
          type="text"
          required
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder={t.placeholders.title}
          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB] outline-none"
        />
      </div>

      {/* Review Content */}
      <div>
        <label className="block text-sm font-medium text-[#0f172a] mb-2">{t.content}</label>
        <textarea
          required
          rows={4}
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          placeholder={t.placeholders.content}
          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB] outline-none resize-none"
        />
      </div>

      {/* Error */}
      {error && (
        <p className="text-red-600 text-sm">{error}</p>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full btn-accent flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {isSubmitting ? (
          t.submitting
        ) : (
          <>
            <Send size={18} />
            {t.submit}
          </>
        )}
      </button>
    </form>
  );
};
